import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Holds the correlation id used for the audit `X-Correlation-Id` header, which the interceptor
 * attaches to every admin-API request (reads included, not only writes). Regenerated on each
 * route navigation so the burst of API calls triggered by one screen/gesture shares a single
 * correlation id.
 *
 * Seeded eagerly so a value is available before the first navigation. Register
 * (touch) this service at startup so it subscribes to router events from the
 * initial navigation onward (see `app.config.ts`).
 */
@Injectable({
  providedIn: 'root',
})
export class AuditCorrelationService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private currentId = crypto.randomUUID();

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => (this.currentId = crypto.randomUUID()));
  }

  get correlationId(): string {
    return this.currentId;
  }
}
